import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import { FiCheckCircle, FiChevronDown, FiPrinter } from 'react-icons/fi';
import { Dropdown } from '@/components/custom/dropdown/dropdown';
import moment from 'moment';
import { confirm } from '@/lib/confirm';
import toast from 'react-hot-toast';
import { useClient } from 'jsonapi-react';
import usePermission from '@/hooks/usePermission';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconVerified } from '@/components/custom/icon';
import { useSession } from '@/hooks/useSession';
import IconExams from '@/components/custom/icons/IconExams';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ButtonGroup } from '@/components/ui/button-group';
import { Button } from '@/components/ui/button';

export default function ExamCard(props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id, exam, handleDeleteExam, activeTabs, filteredExams } = props;
  const client = useClient();
  const currentUser = useSession().session.user;

  const examForm = useFormik({
    initialValues: {
      id: exam?.id,
      published: exam?.published
    }
  });

  useEffect(() => {
    examForm.setValues({
      id: exam?.id,
      published: exam?.published
    });
  }, [filteredExams]);

  const deleteExam = async () => {
    try {
      await client.delete(['question_books', exam.id]);
      handleDeleteExam(exam.id);
      toast.success(t('toast.successDeleteExam'));
    } catch (e) {
      console.error(e);
      toast.error(t('toast.errorDeleteExam'));
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteExam'),
      description: t('projects.warningDeleteCannotBeUndoneText'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      deleteExam();
    }
  };

  const updateExamPublicity = async () => {
    try {
      await client.mutate(['question_books', exam.id], {
        published: !examForm.values.published
      });
      if (!examForm.values.published) {
        toast.success(t('toast.successPublishExam'));
      } else {
        toast.success(t('toast.successUnpublishExam'));
      }
    } catch {
      toast.error(t('toast.errorPublishExam'));
    }
  };

  const shouldRenderOptions = useMemo(() => {
    if (exam?.['authored-by-user'] === true) {
      if (exam?.creator?.id === currentUser.id || currentUser?.profileNames?.includes('Administrador')) {
        return true;
      } else return false;
    }

    if (exam?.['authored-by-user'] === false) {
      if (usePermission('Editar provas', 'Provas e simulados') || usePermission('Excluir provas', 'Provas e simulados')) {
        return true;
      } else return false;
    }
  }, [exam]);

  const hasPermissionToPublish = usePermission('Gerenciar provas', 'Provas e simulados');
  const isCreatorOrAdmin = !exam?.['authored-by-user'] || (exam?.['authored-by-user'] && currentUser.id === exam?.creator.id) || currentUser?.profileNames.includes('Administrador');

  return (
    <div className="card">
      <div
        className="card__inner-header"
        style={{ minHeight: 32 }}
      >
        {hasPermissionToPublish && isCreatorOrAdmin && (
          <div
            className="form__check form__switch !mb-0"
            style={{ flexGrow: '1' }}
          >
            <input
              className="form__check-input"
              id={`published-${id}`}
              name={`published-${id}`}
              type="checkbox"
              defaultChecked={examForm.values.published}
              onClick={() => {
                if (exam?.['question-count'] > 0) {
                  examForm.setFieldValue('published', !examForm.values.published);
                  updateExamPublicity();
                } else {
                  toast.error('Adicione questões para publicar esta prova');
                }
              }}
            />
            <label htmlFor={`publish-${id}`}>{t('exams.publishExam')}</label>
          </div>
        )}

        {exam?.['question-count'] > 0 && exam?.['question-count'] <= 100 && (
          <button
            className="card__print-btn"
            title={t('button.printExam')}
            onClick={() => navigate(`/avaliacoes/provas/impressao/${exam?.id}`)}
          >
            <FiPrinter />
          </button>
        )}
      </div>

      <div className="card__container">
        <span className="card__icon-wrapper card__icon-wrapper--sm">
          {exam?.['official-content'] && <IconVerified />}
          <IconExams className="card__icon card__icon--md" />
        </span>

        <div className="card__content">
          <div className="card__header">
            <div className="card__inner-header">
              <h2
                className="card__title"
                title="Nome da prova"
              >
                {exam?.title}
              </h2>
            </div>

            {exam.status == 'Não realizada' && <span className="badge badge--tiny badge--warning">{t('exams.statusUnrealized')}</span>}
            {exam.status == 'Realizada' && <span className="badge badge--tiny badge--success">{t('exams.statusDone')}</span>}
            {exam.status == 'Em andamento' && <span className="badge badge--tiny badge--warning">{t('exams.inProgress')}</span>}
          </div>

          <div className="card__body">
            <div className="card__badges card__badges--one-line">
              {exam?.taxonomies.length > 0 && (
                <div className="card__badges card__badges--one-line mb-0">
                  {exam?.taxonomies.length <= 2 &&
                    exam?.taxonomies.map(t => (
                      <span
                        className="badge badge--primary"
                        key={t.id}
                        title={t.name}
                      >
                        {t.name}
                      </span>
                    ))}

                  {exam?.taxonomies.length > 2 && (
                    <>
                      {exam?.taxonomies.slice(0, 2).map(t => (
                        <span
                          className="badge badge--primary"
                          key={t.id}
                          title={t.name}
                        >
                          {t.name}
                        </span>
                      ))}
                      <span
                        className="badge badge--more"
                        title={exam?.taxonomies.slice(2).map(t => ` ${t.name}`)}
                      >
                        +{exam?.taxonomies.length - 2}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="card__columns">
              <p className="card__count">
                <span className="card__count-label">{t('textsCommon.questions')}</span>
                <FiCheckCircle className="card__icon" /> {exam?.['question-count']}
              </p>

              {exam?.['published-at'] && (
                <p className="card__count">
                  <span>{t('exams.labelPublishedAt')}</span>
                  {moment(exam?.['published-at']).format(t('datesForm.formatDate'))}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card__footer">
        {exam.status === 'Realizada' && usePermission('Visualizar resultados', 'Provas e simulados') ? (
          <ButtonGroup className="w-full">
            {usePermission('Gerenciar provas', 'Provas e simulados') && (
              <Button
                variant="outline"
                size="sm"
                fullWidth
                asChild
              >
                <Link
                  state={{ activeTabs: activeTabs }}
                  to={`/provas/gerenciar/${exam.id}`}
                  className="btn btn--outline btn--small"
                >
                  {t('button.manage')}
                </Link>
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/lista-resultado-das-provas/${exam.id}`)}
            >
              Resultados
            </Button>
          </ButtonGroup>
        ) : null}
        {exam.status === 'Em andamento' && usePermission('Visualizar resultados', 'Provas e simulados') ? (
          <ButtonGroup className="w-full">
            {usePermission('Gerenciar provas', 'Provas e simulados') && (
              <Button
                variant="outline"
                size="sm"
                fullWidth
                asChild
              >
                <Link
                  to={`/provas/gerenciar/${exam.id}`}
                  className="btn btn--outline btn--small"
                >
                  {t('button.manage')}
                </Link>
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/lista-resultado-das-provas/${exam.id}`)}
            >
              Resultados
            </Button>

            {shouldRenderOptions ? (
              <DropdownMenu className="btn--small btn btn--outline">
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                  >
                    <FiChevronDown size={20} />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent>
                  {exam?.['question-count'] > 0 && exam?.['question-count'] <= 100 && usePermission('Editar provas', 'Provas e simulados') && <Dropdown.Item to={`/avaliacoes/provas/offline/${exam.id}`}>{t('button.offlineExam')}</Dropdown.Item>}
                  {usePermission('Editar provas', 'Provas e simulados') && <Dropdown.Item to={`/provas/criar/curso/${exam.id}`}>{t('button.edit')}</Dropdown.Item>}
                  {usePermission('Excluir provas', 'Provas e simulados') && <Dropdown.Item onClick={handleDelete}>{t('button.delete')}</Dropdown.Item>}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </ButtonGroup>
        ) : null}
        {exam.status === 'Não realizada' && usePermission('Criar provas e simulados', 'Provas e simulados') ? (
          <ButtonGroup className="w-full">
            {usePermission('Gerenciar provas', 'Provas e simulados') && (
              <Button
                variant="outline"
                size="sm"
                fullWidth
                asChild
              >
                <Link
                  to={`/provas/gerenciar/${exam.id}`}
                  className="btn btn--outline btn--small"
                >
                  {t('button.manage')}
                </Link>
              </Button>
            )}

            {usePermission('Editar provas', 'Provas e simulados') || usePermission('Excluir provas', 'Provas e simulados') ? (
              <DropdownMenu className="btn--small btn btn--outline">
                <DropdownMenuTrigger>
                  <FiChevronDown />
                </DropdownMenuTrigger>

                <DropdownMenuContent>
                  {exam?.['question-count'] > 0 && exam?.['question-count'] <= 100 && usePermission('Editar provas', 'Provas e simulados') && <DropdownMenuItem to={`/avaliacoes/provas/offline/${exam.id}`}>{t('button.offlineExam')}</DropdownMenuItem>}
                  {usePermission('Editar provas', 'Provas e simulados') && <DropdownMenuItem to={`/provas/criar/curso/${exam.id}`}>{t('button.edit')}</DropdownMenuItem>}
                  {usePermission('Excluir provas', 'Provas e simulados') && <DropdownMenuItem onClick={handleDelete}>{t('button.delete')}</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </ButtonGroup>
        ) : null}
      </div>
    </div>
  );
}
